<template>
  <div class="default-modal sample-approval-modal">
    <el-dialog
      top="5vh"
      width="900px"
      :model-value="visible"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      @close="onClose"
    >
      <div class="sa-header">
        <div>
          <h4 class="sa-title">Sample Approval</h4>
          <p class="sa-subtitle">Order: {{ orderId || "--" }}</p>
        </div>
        <button type="button" class="sa-close" aria-label="Close" @click="onClose">
          <i class="el-icon-close"></i>
        </button>
      </div>

      <div class="sa-body">
        <div class="sa-field">
          <label class="sa-label">Select Action <span class="required">*</span></label>
          <div class="option-cards">
            <div
              v-for="opt in actionOptions"
              :key="opt.value"
              class="option-card"
              :class="[opt.variant, { selected: action === opt.value }]"
              @click="action = opt.value"
            >
              <span class="radio-dot"></span>
              <div>
                <h5>{{ opt.title }}</h5>
                <p>{{ opt.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="sa-field" v-if="action === 'approve'">
          <label class="sa-label">Approval Type <span class="required">*</span></label>
          <div class="option-cards">
            <div
              v-for="opt in approvalTypeOptions"
              :key="opt.value"
              class="option-card"
              :class="{ selected: approvalType === opt.value }"
              @click="approvalType = opt.value"
            >
              <span class="radio-dot"></span>
              <div>
                <h5>{{ opt.title }}</h5>
                <p>{{ opt.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="sa-field sa-field-samples" v-if="action === 'approve' && approvalType === 'existing_sample'">
          <label class="sa-label">
            Select Existing Sample(s) <span class="required">*</span>
            <span class="sa-hint">(select one or more)</span>
          </label>
          <div v-if="existingOrders.length" class="sample-list">
            <div
              v-for="order in existingOrders"
              :key="getOrderKey(order)"
              class="option-card sample-card"
              :class="{ selected: isOrderSelected(order) }"
              @click="toggleOrder(order)"
            >
              <span class="check-box"></span>
              <div>
                <h5 class="sample-id">{{ order.order_id }}</h5>
                <p>Collection Date: {{ momentWithTimezone(order.collection_date) || "--" }}</p>
                <div v-if="order.tube_names && order.tube_names.length" class="tube-chips">
                  <span
                    v-for="(tube, tIndex) in order.tube_names"
                    :key="`${getOrderKey(order)}-tube-${tIndex}`"
                    class="tube-chip"
                  >{{ tube }}</span>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="sa-empty">No previously collected samples available.</p>
        </div>

        <div class="sa-field">
          <label class="sa-label">Notes <span class="required">*</span></label>
          <el-input
            v-model="notes"
            type="textarea"
            :rows="2"
            resize="none"
            class="sa-textarea"
            :placeholder="notesPlaceholder"
          />
        </div>
      </div>

      <div class="sa-footer">
        <el-button class="sa-btn sa-btn-cancel" @click="onClose">Cancel</el-button>
        <el-button
          class="sa-btn sa-btn-confirm"
          :class="action === 'reject' ? 'is-danger' : 'is-success'"
          :loading="submitting"
          :disabled="!isFormValid"
          @click="onSubmit"
        >
          {{ action === "reject" ? "Confirm Rejection" : "Confirm Approval" }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "SampleApprovalDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: String,
      default: "",
    },
    existingOrders: {
      type: Array,
      default: () => [],
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "submit"],
  data() {
    return {
      action: "approve",
      approvalType: "new_sample",
      existingOrderIds: [],
      notes: "",
      actionOptions: [
        { value: "approve", title: "Approve", description: "Approve and continue" },
        { value: "reject", title: "Reject", description: "Reject this sample", variant: "danger" },
      ],
      approvalTypeOptions: [
        { value: "new_sample", title: "New Sample", description: "Collect a new sample" },
        { value: "existing_sample", title: "Existing Sample", description: "Use a previously collected sample" },
      ],
    };
  },
  computed: {
    notesPlaceholder() {
      if (this.action === "reject") return "Enter reason for rejection...";
      return this.approvalType === "existing_sample"
        ? "Enter notes for using existing sample..."
        : "Enter notes for new sample collection...";
    },
    isFormValid() {
      if (!this.notes || !this.notes.trim()) return false;
      if (this.action === "approve" && this.approvalType === "existing_sample" && !this.existingOrderIds.length) {
        return false;
      }
      return true;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
  },
  methods: {
    resetForm() {
      this.action = "approve";
      this.approvalType = "new_sample";
      this.existingOrderIds = [];
      this.notes = "";
    },
    getOrderKey(order) {
      return order._id || order.order_id;
    },
    isOrderSelected(order) {
      return this.existingOrderIds.includes(this.getOrderKey(order));
    },
    toggleOrder(order) {
      const key = this.getOrderKey(order);
      this.existingOrderIds = this.isOrderSelected(order)
        ? this.existingOrderIds.filter((id) => id !== key)
        : [...this.existingOrderIds, key];
    },
    onClose() {
      this.$emit("close");
    },
    onSubmit() {
      if (!this.isFormValid) return;
      this.$emit("submit", { action: this.action, payload: this.buildPayload() });
    },
    buildPayload() {
      const notes = this.notes.trim();

      if (this.action === "reject") {
        return { status: "REJECTED", sample_approval_notes: notes };
      }

      if (this.approvalType === "new_sample") {
        return {
          sample_pending: false,
          sample_approval: false,
          sample_approval_notes: notes,
        };
      }

      const selected = this.existingOrders.filter((order) => this.isOrderSelected(order));
      return {
        sample_pending: false,
        sample_approval: true,
        sample_approval_data: selected.map((order) => {
          const entry = {
            _id: order._id,
            order_id: order.order_id,
            tube_names: order.tube_names || [],
            collection_date: order.collection_date,
          };
          if (order.tube_accession) entry.tube_accession = order.tube_accession;
          return entry;
        }),
        sample_approval_notes: notes,
        collection_date: this.getEarliestCollectionDate(selected),
      };
    },
    getEarliestCollectionDate(orders) {
      const dates = orders.map((order) => order.collection_date).filter(Boolean);
      if (!dates.length) return undefined;
      return dates.reduce((earliest, date) =>
        new Date(date) < new Date(earliest) ? date : earliest
      );
    },
  },
};
</script>

<style lang="scss" scoped>
$green: #16a34a;
$green-bg: #effcf2;
$red: #e5484d;
$red-bg: #fff1f1;
$border: #e5e7eb;
$text: #111827;
$muted: #6b7280;

.sample-approval-modal {
  :deep(.el-overlay-dialog) {
    overflow: hidden;
  }

  // Keep the whole popup inside the viewport; only the sample list scrolls.
  :deep(.el-dialog) {
    display: flex;
    flex-direction: column;
    max-width: calc(100vw - 32px);
    max-height: 90vh;
    margin-bottom: 0;
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.18);
  }

  :deep(.el-dialog__body) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding: 0;
    overflow: hidden;
  }
}

.sa-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px 14px;
  border-bottom: 1px solid #f0f1f3;

  .sa-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $text;
  }

  .sa-subtitle {
    margin: 4px 0 0;
    font-size: 12px;
    color: $muted;
  }

  .sa-close {
    background: none;
    border: none;
    padding: 4px;
    margin-top: 6px;
    cursor: pointer;
    color: #9ca3af;
    font-size: 16px;
    line-height: 1;

    &:hover {
      color: $text;
    }
  }
}

.sa-header,
.sa-footer {
  flex-shrink: 0;
}

.sa-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 18px 24px 20px;
  overflow: hidden;
}

.sa-field {
  flex-shrink: 0;
}

.sa-field-samples {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.sa-field + .sa-field {
  margin-top: 18px;
}

.sa-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: $text;

  .required {
    color: $red;
  }
}

.option-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.option-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid $border;
  border-radius: 10px;
  background: #f9fafb;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  h5 {
    margin: 0 0 2px;
    font-size: 13px;
    font-weight: 600;
    color: $text;
  }

  p {
    margin: 0;
    font-size: 11px;
    color: $muted;
  }

  .radio-dot {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 1px;
    border: 1.5px solid #d1d5db;
    border-radius: 50%;
    background: #fff;
    position: relative;
  }

  &:hover {
    border-color: #c7cbd1;
  }

  &.selected {
    border: 1.5px solid $green;
    background: $green-bg;

    .radio-dot {
      border-color: $green;

      &::after {
        content: "";
        position: absolute;
        inset: 3px;
        border-radius: 50%;
        background: $green;
      }
    }
  }

  &.danger.selected {
    border-color: $red;
    background: $red-bg;

    .radio-dot {
      border-color: $red;

      &::after {
        background: $red;
      }
    }
  }
}

.sa-hint {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 400;
  color: $muted;
}

.sample-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-content: start;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 96px;
  max-height: 212px;
  overflow-y: auto;
  padding-right: 4px;
}

@media (max-width: 640px) {
  .option-cards,
  .sample-list {
    grid-template-columns: 1fr;
  }
}

.sample-card {
  .sample-id {
    font-size: 14px;
  }

  .check-box {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    border: 1.5px solid #d1d5db;
    border-radius: 4px;
    background: #fff;
    position: relative;
  }

  &.selected .check-box {
    border-color: $green;
    background: $green;

    &::after {
      content: "";
      position: absolute;
      left: 4px;
      top: 1px;
      width: 4px;
      height: 8px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
}

.tube-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.tube-chip {
  padding: 3px 8px;
  border-radius: 12px;
  background: #e8ece9;
  font-size: 11px;
  font-weight: 500;
  color: #374151;
  text-transform: uppercase;
}

.sa-empty {
  margin: 0;
  padding: 14px 16px;
  border: 1px dashed $border;
  border-radius: 10px;
  font-size: 12px;
  color: $muted;
}

.sa-textarea :deep(.el-textarea__inner) {
  border-radius: 10px;
  border-color: $border;
  background: #f9fafb;
  padding: 10px 12px;
  font-size: 13px;

  &:focus {
    border-color: $green;
    background: #fff;
  }
}

.sa-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #f0f1f3;

  .sa-btn {
    min-width: 84px;
    height: 36px;
    padding: 0 20px;
    border-radius: 18px;
    font-size: 13px;
    font-weight: 500;
  }

  .sa-btn-cancel {
    background: #fff;
    border: 1px solid $border;
    color: $text;

    &:hover,
    &:focus {
      background: #f9fafb;
      border-color: #d1d5db;
      color: $text;
    }
  }

  .sa-btn-confirm {
    border: none;
    color: #fff;
    font-weight: 600;

    &.is-success {
      background: $green;
      &:hover,
      &:focus {
        background: darken($green, 5%);
      }
    }

    &.is-danger {
      background: $red;
      &:hover,
      &:focus {
        background: darken($red, 5%);
      }
    }

    &.is-disabled,
    &.is-disabled:hover {
      opacity: 0.5;
      color: #fff;
    }

    &.is-success.is-disabled,
    &.is-success.is-disabled:hover {
      background: $green;
    }

    &.is-danger.is-disabled,
    &.is-danger.is-disabled:hover {
      background: $red;
    }
  }
}
</style>
